import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Minus, Plus, ShoppingBag, Truck, RefreshCcw, ShieldCheck } from "lucide-react";
import { api, imgUrl, formatINR } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { ProductCard } from "../components/ProductCard";
import { QuickView } from "../components/QuickView";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { toast } from "sonner";

export default function ProductPage() {
  const { id } = useParams();
  const { addToCart, toggleWishlist, wishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState("");
  const [selections, setSelections] = useState({});
  const [quick, setQuick] = useState(null);

  useEffect(() => {
    setLoading(true);
    setImg(0);
    setQty(1);
    setVariant("");
    setSelections({});
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      const defaults = {};
      (r.data.variant_groups || []).forEach((g) => {
        const def = (g.options || []).find((o) => o.is_default && o.is_active);
        if (def) defaults[g.name] = def.label;
      });
      setSelections(defaults);
    }).catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl animate-pulse px-4 py-12 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="aspect-[3/4] rounded-3xl bg-secondary/60" />
          <div className="space-y-4 pt-6"><div className="h-8 w-2/3 rounded bg-secondary/60" /><div className="h-5 w-1/4 rounded bg-secondary/60" /><div className="h-24 rounded bg-secondary/60" /></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-28 text-center" data-testid="product-not-found">
        <Seo title="Product unavailable — Anaya Fashion & Gifts" path="/shop" noindex />
        <h1 className="font-display text-4xl">This piece has found a home</h1>
        <p className="mt-3 text-sm font-light text-muted-foreground">It&#8217;s no longer available — but so many pretty things are.</p>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-foreground px-7 py-3 text-xs tracking-[0.2em] text-white uppercase hover:bg-primary">Shop all</Link>
      </div>
    );
  }

  const wished = wishlist.some((p) => p.id === product.id);
  const hasVariants = product.variants?.length > 0;
  const groups = (product.variant_groups || []).map((g) => ({ ...g, options: (g.options || []).filter((o) => o.is_active) })).filter((g) => g.options.length);
  const selectedOptions = groups.map((g) => g.options.find((o) => o.label === selections[g.name])).filter(Boolean);
  const missingGroups = groups.filter((g) => !selections[g.name]);
  const variantPrice = selectedOptions.reduce((acc, o) => (o.price != null ? o.price : acc), product.price);
  const variantStock = selectedOptions.reduce((acc, o) => (o.stock != null ? Math.min(acc, o.stock) : acc), product.stock);
  const colourImages = selectedOptions.find((o) => o.images?.length)?.images;
  const gallery = colourImages || product.images || [];

  const add = () => {
    if (groups.length && missingGroups.length) {
      toast.error(`Please choose a ${missingGroups[0].name} first`);
      return;
    }
    if (hasVariants && !variant) {
      toast.error("Please choose your ring design first");
      return;
    }
    const variantStr = groups.length ? groups.map((g) => selections[g.name]).join(" / ") : variant;
    addToCart(groups.length ? { ...product, price: variantPrice } : product, qty, variantStr);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="product-page">
      <Seo
        title={`${product.name} — Anaya Fashion & Gifts`}
        description={(product.description || `${product.name} at Anaya Fashion & Gifts`).slice(0, 158)}
        path={`/product/${product.id}`}
        image={product.images?.[0]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "BreadcrumbList", "itemListElement": [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://anayafashion.store/" },
              { "@type": "ListItem", position: 2, name: "Shop", item: "https://anayafashion.store/shop" },
              { "@type": "ListItem", position: 3, name: product.name },
            ] },
            { "@type": "Product",
              name: product.name,
              description: product.description || product.name,
              image: (product.images || []).map((u) => `https://anayafashion.store${u}`),
              sku: product.sku,
              offers: {
                "@type": "Offer",
                url: `https://anayafashion.store/product/${product.id}`,
                priceCurrency: "INR",
                price: product.price,
                availability: variantStock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              } },
          ],
        }}
      />
      <nav className="text-[11px] tracking-[0.2em] text-muted-foreground uppercase">
        <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/shop" className="hover:text-primary">Shop</Link> / <span className="text-foreground">{product.name}</span>
      </nav>
      <div className="mt-6 grid gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="product-img-frame relative overflow-hidden bg-white shadow-[0_25px_70px_-30px_rgba(61,43,51,0.4)]" data-testid="product-gallery">
            {gallery[img] ? (
              <img src={imgUrl(gallery[img])} alt={product.name} className="aspect-[3/4] w-full object-cover" data-testid="product-main-image" />
            ) : (
              <div className="flex aspect-[3/4] w-full items-center justify-center bg-secondary/40 font-display text-8xl text-primary/40">A</div>
            )}
            {product.is_bestseller && <span className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[10px] tracking-widest text-white uppercase">Bestseller</span>}
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 flex gap-2">
              {gallery.map((u, i) => (
                <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setImg(i)}
                  className={`h-20 w-16 overflow-hidden rounded-xl ring-2 transition-all ${i === img ? "ring-primary" : "ring-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={imgUrl(u)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        <Reveal delay={0.15}>
          <div className="lg:pt-4">
            <h1 className="font-display text-3xl leading-tight sm:text-4xl" data-testid="product-name">{product.name}</h1>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="text-2xl font-medium text-primary" data-testid="product-price">{variantPrice > 0 ? formatINR(variantPrice) : "Price on request"}</span>
              {variantPrice > 0 && product.compare_at_price > variantPrice && (
                <>
                  <span className="text-base text-muted-foreground line-through">{formatINR(product.compare_at_price)}</span>
                  <span className="rounded-full bg-accent px-2.5 py-0.5 text-[10px] tracking-widest text-accent-foreground uppercase">
                    Save {Math.round((1 - variantPrice / product.compare_at_price) * 100)}%
                  </span>
                </>
              )}
            </div>
            <p className="mt-5 text-sm font-light leading-relaxed text-muted-foreground" data-testid="product-description">{product.description}</p>

            {groups.length > 0 && (
              <div className="mt-6 space-y-4" data-testid="variant-groups-section">
                {groups.map((g) => (
                  <div key={g.name} className="rounded-2xl border border-primary/25 bg-white/60 p-5" data-testid={`variant-group-${g.name}`}>
                    <p className="text-xs font-medium tracking-[0.25em] text-foreground uppercase">Choose {g.name}{selections[g.name] ? `: ${selections[g.name]}` : ""}</p>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`variant-options-${g.name}`}>
                      {g.options.map((o) => {
                        const outOfStock = o.stock != null && o.stock <= 0;
                        return (
                          <button key={o.label} data-testid={`variant-${g.name}-${o.label}`} disabled={outOfStock}
                            onClick={() => { setSelections((s) => ({ ...s, [g.name]: o.label })); setImg(0); }}
                            className={`rounded-full border px-4 py-2.5 text-xs font-medium tracking-wide transition-all duration-200 ${selections[g.name] === o.label ? "scale-105 border-primary bg-primary text-white shadow-lg" : "border-input bg-white/80 hover:border-primary/60 hover:bg-secondary"} ${outOfStock ? "cursor-not-allowed opacity-40 line-through" : ""}`}>
                            {o.label}{o.price != null && o.price !== product.price ? ` · ${formatINR(o.price)}` : ""}
                          </button>
                        );
                      })}
                    </div>
                    {g.options.some((o) => o.stock != null && o.stock <= 0) && (
                      <p className="mt-2 text-[11px] font-light text-muted-foreground">Crossed-out options are out of stock.</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {hasVariants && (
              <div className="mt-6 rounded-2xl border border-primary/25 bg-white/60 p-5" data-testid="variant-section">
                <p className="text-xs font-medium tracking-[0.25em] text-foreground uppercase">Choose your ring design</p>
                <p className="mt-1 text-[11px] font-light text-muted-foreground">Same price for every design — match the letter on the tray photo.</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="variant-options">
                  {product.variants.map((v) => (
                    <button key={v} data-testid={`variant-${v}`} onClick={() => setVariant(v)}
                      className={`h-11 w-11 rounded-full border text-sm font-medium transition-all duration-200 ${variant === v ? "scale-110 border-primary bg-primary text-white shadow-lg" : "border-input bg-white/80 hover:border-primary/60 hover:bg-secondary"}`}>
                      {v}
                    </button>
                  ))}
                </div>
                {variant && <p className="mt-3 text-xs text-primary" data-testid="variant-selected">Selected design: <strong>{variant}</strong> — {formatINR(product.price)} for one ring</p>}
              </div>
            )}

            <p className={`mt-4 text-xs tracking-[0.2em] uppercase ${variantStock > 0 ? "text-green-700" : "text-destructive"}`} data-testid="product-stock">
              {variantStock > 0 ? `${variantStock} in stock` : "Out of stock"}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <div className="flex items-center rounded-full border border-input bg-white/70">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" aria-label="Decrease quantity"><Minus size={15} /></button>
                <span className="w-9 text-center" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(Math.min(20, qty + 1))} className="p-3" aria-label="Increase quantity"><Plus size={15} /></button>
              </div>
              <button data-testid="add-to-cart-btn" onClick={add} disabled={variantStock <= 0}
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-xs font-medium tracking-[0.2em] text-white uppercase transition-colors hover:bg-primary disabled:opacity-40 sm:flex-none">
                <ShoppingBag size={15} /> Add to bag
              </button>
              <button data-testid="wishlist-toggle-btn" onClick={() => toggleWishlist(product)} aria-label="Toggle wishlist"
                className={`rounded-full border p-3.5 transition-colors ${wished ? "border-primary bg-primary text-white" : "border-input bg-white/70 hover:bg-secondary"}`}>
                <Heart size={16} fill={wished ? "currentColor" : "none"} />
              </button>
            </div>

            <p className="mt-3 text-xs font-light text-muted-foreground" data-testid="shipping-note">+ ₹70 shipping (prepaid) or ₹120 (COD) at checkout</p>

            <div className="mt-9 space-y-3 border-t border-input pt-6 text-sm font-light">
              <p className="flex items-center gap-3"><Truck size={16} className="text-primary" /> Ships in 2–4 days · COD available</p>
              <p className="flex items-center gap-3"><RefreshCcw size={16} className="text-primary" /> Easy 3-day exchange on unused items</p>
              <p className="flex items-center gap-3"><ShieldCheck size={16} className="text-primary" /> Pay via UPI, bank transfer or cash on delivery</p>
            </div>
          </div>
        </Reveal>
      </div>

      {product.related?.length > 0 && (
        <section className="mt-20" data-testid="related-products">
          <Reveal><h2 className="font-display text-2xl sm:text-3xl">You may also love</h2></Reveal>
          <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4">
            {product.related.map((p, i) => <ProductCard key={p.id} product={p} index={i} onQuickView={setQuick} />)}
          </div>
        </section>
      )}

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
}
